import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CollectionsService } from '../../../services/collections.service';
import { DocumentService } from '../../../services/document.service';
import { Collection } from '../../../interfaces/collection.interface';
import { HttpResponse } from '@angular/common/http';

interface ManualDocumentRow {
  id: string;
  category: string;
  documentName: string;
  collectionName: string;
  baseName: string;
}

@Component({
  selector: 'app-manual-documents-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './manual-documents-modal.component.html',
  styleUrl: './manual-documents-modal.component.css'
})
export class ManualDocumentsModalComponent implements OnInit {
  searchTerm = '';
  isLoading = false;
  rows: ManualDocumentRow[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private collectionsService: CollectionsService,
    private documentService: DocumentService
  ) {}

  ngOnInit(): void {
    this.loadDocuments();
  }

  get filteredRows(): ManualDocumentRow[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.rows;
    return this.rows.filter(r =>
      r.category.toLowerCase().includes(term) ||
      r.documentName.toLowerCase().includes(term)
    );
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onPreview(row: ManualDocumentRow): void {
    this.documentService.downloadDocument(row.collectionName, row.documentName, true).subscribe({
      next: (response: HttpResponse<Blob>) => {
        const blob = response.body;
        if (!blob) return;
        const previewBlob = this.ensureBlobMimeType(blob, row.documentName);
        const url = window.URL.createObjectURL(previewBlob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      }
    });
  }

  onDownload(row: ManualDocumentRow): void {
  this.documentService
    .downloadDocument(
      row.collectionName,
      row.documentName,
      false
    )
    .subscribe({
      next: (response: HttpResponse<Blob>) => {
        const blob = response.body;
        if (!blob) return;

        let filename = row.documentName;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches?.[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        const downloadBlob = this.ensureBlobMimeType(blob, filename);

        const url = window.URL.createObjectURL(downloadBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },

      error: (error) => {
        console.error('Download failed:', error);
      }
    });
}

  private ensureBlobMimeType(blob: Blob, filename: string): Blob {
    if (blob.type && blob.type !== 'application/octet-stream') return blob;

    const extension = (filename.split('.').pop() || '').toLowerCase();
    const extensionMimeMap: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    const mimeType = extensionMimeMap[extension] || 'application/octet-stream';
    return new Blob([blob], { type: mimeType });
  }

  private loadDocuments(): void {
    this.isLoading = true;
    this.collectionsService.getCollections().subscribe({
      next: (response) => {
        const collections: Collection[] = response?.data ?? [];
        this.rows = collections.flatMap((collection) =>
          (collection.documents || []).map((doc) => ({
            id: doc.id,
            category: doc.category || '-',
            documentName: doc.name || '',
            collectionName: collection.name,
            baseName: doc.baseName || doc.name
          }))
        );
        this.isLoading = false;
      },
      error: () => {
        this.rows = [];
        this.isLoading = false;
      }
    });
  }
}
